/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useMutation } from '@apollo/client';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Avatar,
	Box,
	Button,
	Flex,
	FormLabel,
	Text,
	useDisclosure,
	useToast,
	Wrap,
	WrapItem,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import moment from 'moment';
import 'moment/locale/en-gb';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import FileFormField from '../../components/form/FileFormField';
import InputNumberFormField from '../../components/form/InputNumberFormField';
import RadioFormField from '../../components/form/RadioFormField';
import TextAreaFormField from '../../components/form/TextAreaFormField';
import LoadingOverlayText from '../../components/LoadingOverlay/LoadingOverlayText';
import c from '../../constant';
import DateTimeFormField from '../../utils/DateTimeFormField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusSquare, faPlusSquare } from '@fortawesome/free-solid-svg-icons';
import LabelDetail from '../../components/LabelDetail';
import SelectUserAllModal from '../../components/Modal/SelectUserShowAllModal';
import useStore from '../../store';
import { getMissionRecordAmountOfDays } from '../../utils/mission-record-utils';

const CREATE_MUTATION = gql`
	mutation create(
		$category: MissionRecordCategoryEnumType!
		$reason: String!
		$amountOfDays: Float!
		$fromDate: DateTime!
		$toDate: DateTime!
		$attachmentFile: Upload
		$attachmentFileSize: Float
		$participants: [String!]
	) {
		createMissionRecord(
			recordInput: {
				category: $category
				reason: $reason
				amountOfDays: $amountOfDays
				fromDate: $fromDate
				toDate: $toDate
				participants: $participants
			}
			attachmentFile: $attachmentFile
			attachmentFileSize: $attachmentFileSize
		) {
			id
			category
			reason
			amountOfDays
			fromDate
			toDate
			attachmentFile
		}
	}
`;

const LABEL_WIDTH = '169px';

const validationSchema = Yup.object().shape({
	category: Yup.string().oneOf(['RequestForMeetingParticipation', 'Mission', 'AttendanceRequest']).required('required'),
	amountOfDays: Yup.mixed()
		.test('not-empty', 'required', function (value) {
			return !value ? false : true;
		})
		.test('valid', 'invalid', function (value) {
			return value % 0.5 === 0;
		})
		.test('valid-count', 'incorrect mission record amount of day input', function (value) {
			const { fromDate, toDate } = this.parent;
			if (fromDate && toDate) {
				const { count } = getMissionRecordAmountOfDays(fromDate, toDate);
				return count === value;
			}
		}),
	fromDate: Yup.date().required('required'),
	toDate: Yup.date()
		.required('required')
		.test('is-after-from-date', 'not allowed to date before from date', function (value) {
			const { fromDate } = this.parent;
			if (!value) return false;
			return moment(value).isAfter(moment(fromDate));
		}),
	reason: Yup.string().required('required'),
	attachmentFile: Yup.mixed()
		.when('category', {
			is: (category) => ['RequestForMeetingParticipation', 'Mission'].includes(category),
			then: Yup.mixed().required('required'),
			otherwise: Yup.mixed().nullable(),
		})
		.test('fileSize', 'over limit', (value) => {
			if (value) {
				return value?.size <= c.maxFileSize / 20;
			} else {
				return true;
			}
		}),
});

function MissionRecordAdd({ onCloseModal }) {
	moment.locale('en-gb');
	const cancelRef = React.useRef();
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const history = useHistory();
	const toast = useToast();
	const { t } = useTranslation();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [openSelectUserAllModel, setOpenSelectUserAllModel] = useState(false);
	const [participant, setParticipant] = useState([]);
	const [createMutation, { loading }] = useMutation(CREATE_MUTATION);

	useEffect(() => window.scrollTo(0, 0), []);

	useEffect(() => {
		if (user?.id) {
			setParticipant([
				{
					id: user?.id,
					fullNameKhmer: user?.fullNameKhmer,
					fullName: user?.fullName,
					avatar: user?.avatar,
				},
			]);
		}
	}, [user?.id]);

	const hideAddButton = user?.canApproveMissionRecord === true && ['2', '3', '4'].includes(user?.position);
	const dgHideAddButton = user?.position === '1';

	async function submit(values) {
		const variables = {
			category: values?.category || '',
			reason: values?.reason || '-',
			amountOfDays: parseFloat(values?.amountOfDays),
			fromDate: moment(values?.fromDate).toDate(),
			toDate: moment(values?.toDate).toDate(),
			attachmentFile: values?.attachmentFile ? values?.attachmentFile : null,
			attachmentFileSize: values?.attachmentFile?.size ? values?.attachmentFile?.size : null,
			participants: participant?.filter((item) => item?.id !== user?.id)?.map((item) => item?.id) || [],
		};

		try {
			const res = await createMutation({ variables });
			const createdId = res?.data?.createMissionRecord?.id;

			if (!createdId) {
				throw new Error('MISSION_RECORD_NOT_CREATED');
			}

			toast({
				title: t('Mission record created successfully'),
				status: 'success',
				isClosable: true,
				duration: 5000,
				position: 'top-right',
			});

			if (onCloseModal) {
				onCloseModal();
			}

			history.push(`/mission-record/detail?id=${createdId}`);
		} catch (error) {
			toast({
				title: t(error?.message ? error?.message : 'something went wrong'),
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'bottom-right',
			});
		}
	}

	return (
		<>
			<Box w="100%" bg="white" borderRadius={c.borderRadius} ml="1px" pb="32px">
				<Box maxW="510px">
					<Formik
						initialValues={{
							category: '',
							amountOfDays: 1,
							fromDate: new Date(),
							toDate: new Date(),
							reason: '',
							attachmentFile: null,
						}}
						validationSchema={validationSchema}
						onSubmit={submit}
					>
						{({ values, ...props }) => {
							return (
								<Box>
									<Form>
										<RadioFormField
											label="Category"
											name="category"
											radios={[
												{
													label: 'Request for Meeting Participation (Phnom Penh)',
													value: 'RequestForMeetingParticipation',
												},
												{
													label: 'Mission (Domestic and International)',
													value: 'Mission',
												},
												{
													label: 'Attendance Request: Forgot to Check-in/Check-out',
													value: 'AttendanceRequest',
												},
											]}
											required
										/>

										<InputNumberFormField
											{...props}
											label="amountOfDays"
											name="amountOfDays"
											min={0.5}
											max={15}
											step={0.5}
											precision={1}
											required
										/>

										<DateTimeFormField {...props} label={t('Date From')} name="fromDate" value={values?.fromDate} from={true} required />

										<DateTimeFormField {...props} label={t('Date To')} name="toDate" value={values?.toDate} to={true} required />

										<TextAreaFormField {...props} label="Reason" name="reason" required />

										<FileFormField
											{...props}
											label="Attachment File"
											name="attachmentFile"
											maxFileSize={c.maxFileSize / 20}
											required={values?.category === 'RequestForMeetingParticipation' || values?.category === 'Mission' ? true : false}
										/>

										<FormLabel>{t('Participant')}</FormLabel>
										<Flex>
											<LabelDetail
												mb={1}
												mt={0}
												labelWidth={LABEL_WIDTH}
												childDetail={
													<Wrap>
														{participant?.map((item, index) => {
															return (
																<Flex
																	direction="column"
																	justifyContent="center"
																	alignItems="center"
																	key={item?.id || 'u-' + index}
																	mr={2}
																>
																	{
																		<WrapItem className="avatarWraper">
																			<FontAwesomeIcon
																				onClick={() => {
																					if (item?.id !== user?.id) {
																						setParticipant((prev) => prev.filter((u) => u?.id !== item?.id));
																						toast({
																							title: t('You have removed a participant'),
																							status: 'warning',
																							isClosable: true,
																							position: 'top-right',
																						});
																					} else {
																						toast({
																							title: t(`You cannot remove yourself`),
																							status: 'warning',
																							isClosable: true,
																							position: 'bottom-right',
																						});
																					}
																				}}
																				className="remove"
																				icon={faMinusSquare}
																				style={{
																					fontSize: 18,
																					display: 'none',
																					position: 'absolute',
																					zIndex: '100',
																					marginLeft: 32,
																					color: 'red',
																					backgroundColor: 'white',
																					padding: 2,
																					cursor: 'pointer',
																				}}
																			/>
																			<Avatar
																				name={item?.fullNameKhmer}
																				src={item?.avatar?.icon ? c.baseFilePath + item?.avatar?.icon : null}
																			/>
																		</WrapItem>
																	}
																	<Box mt={1}>
																		<Text textAlign="center">{lang === 'kh' ? item?.fullNameKhmer : item?.fullName}</Text>
																	</Box>
																</Flex>
															);
														})}

														{!hideAddButton && !dgHideAddButton && values?.category !== 'AttendanceRequest' && (
															<Flex>
																<Button
																	className="hidden-print"
																	onClick={() => setOpenSelectUserAllModel('viewer')}
																	leftIcon={<FontAwesomeIcon icon={faPlusSquare} style={{ fontSize: 18 }} />}
																	colorScheme="blue"
																	variant="outline"
																>
																	{t('Add')}
																</Button>
															</Flex>
														)}
													</Wrap>
												}
											/>
										</Flex>

										<Button
											id="login-btn"
											variant="solid"
											size="md"
											h="42px"
											w="128px"
											mt={4}
											type="submit"
											borderRadius={c.borderRadius}
											borderColor="red.400"
											isLoading={loading}
											_focus={{ boxShadow: 'none' }}
										>
											{t('Submit')}
										</Button>
									</Form>
								</Box>
							);
						}}
					</Formik>
				</Box>
			</Box>

			{openSelectUserAllModel && (
				<SelectUserAllModal
					cached={false}
					onClose={() => setOpenSelectUserAllModel(false)}
					callback={(e) => {
						if (openSelectUserAllModel === 'viewer') {
							const existed = participant?.some((item) => item?.id === e?.id);
							const isDeputyHead = e?.position === '4' && e?.canApproveMissionRecord === true;

							if (existed) {
								toast({
									title: t('Participant already added'),
									status: 'warning',
									isClosable: true,
									position: 'top-right',
									duration: 3000,
								});

								setOpenSelectUserAllModel(false);
								return;
							}

							// Department account is not allowed to be added as participant
							if (e?.position === '1' || e?.position === '2' || e?.position === '3' || isDeputyHead) {
								toast({
									title:
										e?.position === '1'
											? t('DG account cannot be added as participant')
											: t('Department account cannot be added as participant'),
									status: 'error',
									isClosable: true,
									duration: 3000,
									position: 'top-right',
								});

								setOpenSelectUserAllModel(false);
								return;
							}

							const currentUserDepartment = user?.department;
							const selectedUserDepartment = e?.department;

							if (currentUserDepartment && selectedUserDepartment && currentUserDepartment !== selectedUserDepartment) {
								toast({
									title: t('Cannot add participant from another department'),
									status: 'error',
									isClosable: true,
									duration: 3000,
									position: 'top-right',
								});

								setOpenSelectUserAllModel(false);
								return;
							}

							setParticipant((prev) => [...prev, e]);

							toast({
								title: t('You have added a participant'),
								status: 'success',
								isClosable: true,
								position: 'top-right',
							});
						}

						setOpenSelectUserAllModel(false);
					}}
					viewType="all"
				/>
			)}

			{loading && <LoadingOverlayText />}
		</>
	);
}

export default MissionRecordAdd;
